import React, { MutableRefObject, useCallback, useRef, useState } from 'react'
import { CSSProperties } from 'react'
import { createPortal } from 'react-dom'

import { ChildProps, ClassProps } from '@/common/util/props'
import { getRect } from '@/common/util/rect'
import { useWindowSize } from '@/common/util/window'

import ToolTip, { TooltipPosition } from '@/components/view/atoms/tooltip/Tooltip'

const TOOLTIP_THRESHOLD_WIDTH = 20
const TOOLTIP_MARGIN_PX = 10

interface TooltipProps {
  style: CSSProperties
  position: TooltipPosition
}

const makeToolTipProps = (
  rect: DOMRect,
  windowWidth: number,
  windowHeight: number
): TooltipProps => {
  const position: TooltipPosition =
    rect.left < TOOLTIP_THRESHOLD_WIDTH
      ? TooltipPosition.RIGHT
      : rect.right + TOOLTIP_THRESHOLD_WIDTH > windowWidth
        ? TooltipPosition.LEFT
        : windowHeight / 1.5 > rect.bottom
          ? TooltipPosition.BOTTOM
          : TooltipPosition.TOP

  let left =
    position === TooltipPosition.LEFT
      ? rect.left - TOOLTIP_MARGIN_PX
      : position === TooltipPosition.RIGHT
        ? rect.right + TOOLTIP_MARGIN_PX
        : rect.left + rect.width / 2

  let top =
    position === TooltipPosition.BOTTOM
      ? rect.bottom + TOOLTIP_MARGIN_PX
      : position === TooltipPosition.TOP
        ? rect.top - TOOLTIP_MARGIN_PX
        : rect.top + rect.height / 2

  const tooltipStyle: CSSProperties = {
    left: `${left}px`,
    top: `${top}px`
  }

  return {
    style: tooltipStyle,
    position
  }
}

interface Props extends ChildProps, ClassProps {
  text: string
  off?: boolean
  onClick?(e: React.MouseEvent<HTMLElement>): void
  wrapRef?: MutableRefObject<any>
}

const TooltipWrapper = ({ children, text, off = false, onClick, wrapRef, className }: Props) => {
  const { height, width } = useWindowSize()
  const reference = useRef(null)
  const ref = wrapRef ?? reference
  const [props, setProps] = useState<TooltipProps | null>(null)

  const setToolTip = useCallback(
    (show: boolean) => {
      if (show && !off && ref.current) {
        const rect = getRect(ref)
        if (rect && width != undefined && height != undefined) {
          const tooltipProps = makeToolTipProps(rect, width, height)
          setProps(tooltipProps)
        }
      } else {
        setProps(null)
      }
    },
    [ref, height, width, off]
  )

  const onMouseEnter = useCallback(() => setToolTip(true), [text, off, ref])
  const onMouseLeave = useCallback(() => setToolTip(false), [text, off, ref])

  return (
    <div
      className={className}
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {children}
      {!off &&
        props &&
        props.position &&
        props.style &&
        createPortal(
          <ToolTip text={text} position={props.position} style={props.style} />,
          document.body
        )}
    </div>
  )
}

export default TooltipWrapper
